import { useState } from 'react';
import { Eye, Undo2 } from 'lucide-react';
import { useAuth, useStopImpersonation } from '../useAuth';
import { roleLabel } from '../roleLabels';

/**
 * Режим «смотрим глазами сотрудника» — часть общей шапки на каждом экране.
 * Держит единственный выход обратно к себе, поэтому не зависит от содержимого
 * страницы и остаётся видимым даже при нуле доступных разделов.
 */
export function ImpersonationBanner({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const stop = useStopImpersonation();
  const [error, setError] = useState<string | null>(null);

  const owner = user?.impersonated_by;
  if (!user || !owner) return null;

  async function back() {
    setError(null);
    try {
      await stop.mutateAsync();
      window.location.assign('/');
    } catch {
      setError('Не удалось вернуться. Обновите страницу.');
    }
  }

  return (
    <div data-testid="impersonation-banner" className={compact ? 'flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2' : 'flex min-w-0 basis-full flex-1 items-center gap-3 md:basis-auto'}>
      {!compact && <span className="ops-impersonation-eye hidden h-10 w-10 shrink-0 place-items-center rounded-ops-md text-ops-primary md:grid">
        <Eye size={17} />
      </span>}
      <span className={compact ? 'sr-only' : 'min-w-0 flex-1 leading-tight'}>
        <span className="mb-1 hidden text-[10px] font-bold uppercase tracking-[0.12em] text-ops-primary md:block">
          Режим просмотра
        </span>
        <span className="block text-xs font-semibold text-ops-text md:text-sm">
          Вы смотрите систему в роли «{roleLabel(user.roles)}»
          <span className="font-normal text-ops-text-secondary"> · Вы вошли как {owner.name}</span>
        </span>
        {!compact && error && <span className="mt-1 block text-xs font-medium text-ops-danger">{error}</span>}
      </span>
      <button
        type="button"
        onClick={back}
        disabled={stop.isPending}
        className="ops-impersonation-return flex h-10 shrink-0 items-center gap-1.5 rounded-ops-md bg-ops-primary px-3 text-xs font-bold text-white transition-colors hover:bg-ops-primary-600 disabled:opacity-50 md:px-4 md:text-sm"
      >
        <Undo2 size={15} />
        {stop.isPending ? 'Возвращаю…' : 'Вернуться к себе'}
      </button>
      {compact && error && <span role="alert" className="basis-full text-xs font-medium text-ops-danger">{error}</span>}
    </div>
  );
}
